import React from 'react';
import {WordList} from '../../domain/types';
import WordListRow, {WordListRowActions} from './WordListRow';
import {SearchIcon, XIcon} from '../Icons';

interface WordListTableProps extends WordListRowActions {
  heading: string;
  lists: WordList[];
  variant: 'mine' | 'marketplace';
  /** Ids already in the user's collection — marketplace rows say so instead of offering Add. */
  collectionIds: Set<string>;
  selectedId: string | null;
  isLoading: boolean;
  isBusy: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  empty: {title: string; body: string; action?: React.ReactNode};
}

/**
 * The list of word lists, in the workspace's table shape: a titled panel, one search box, column
 * headings, and a row per item that owns its own actions.
 */
const WordListTable: React.FC<WordListTableProps> = ({
  heading,
  lists,
  variant,
  collectionIds,
  selectedId,
  isLoading,
  isBusy,
  search,
  onSearchChange,
  empty,
  ...rowActions
}) => {
  const hasSearch = search.trim().length > 0;

  return (
    <section className="rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-800">
        <h2 className="text-sm font-semibold text-gray-100">
          {heading}
          {lists.length > 0 && <span className="ml-2 font-normal text-gray-500">{lists.length}</span>}
        </h2>

        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
          <input
            type="search"
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            placeholder="Search word lists…"
            aria-label="Search word lists by name"
            className="w-56 rounded-lg border border-gray-800 bg-gray-950/60 py-1.5 pl-8 pr-7 text-xs
             text-gray-200 placeholder-gray-600 focus:border-indigo-500 focus:outline-none
             focus:ring-1 focus:ring-indigo-500 [&::-webkit-search-cancel-button]:hidden"
          />
          {hasSearch && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-300 cursor-pointer"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </header>

      {lists.length > 0 && (
        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 border-b border-gray-800
         bg-gray-950/40 text-[11px] font-medium uppercase tracking-wide text-gray-500">
          <div className="col-span-6">Name</div>
          <div className="col-span-2">Words</div>
          <div className="col-span-4" />
        </div>
      )}

      {isLoading && lists.length === 0 && (
        <div className="px-4 py-10 text-center text-sm text-gray-500">Loading word lists…</div>
      )}

      {!isLoading && lists.length === 0 && !hasSearch && (
        <div className="flex flex-col items-center gap-1 px-4 py-12 text-center">
          <p className="text-sm font-medium text-gray-200">{empty.title}</p>
          <p className="max-w-md text-sm text-gray-500">{empty.body}</p>
          {empty.action && <div className="mt-3">{empty.action}</div>}
        </div>
      )}

      {!isLoading && lists.length === 0 && hasSearch && (
        <div className="px-4 py-12 text-center">
          <p className="text-sm text-gray-400">No word lists match “{search.trim()}”.</p>
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="mt-2 text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer"
          >
            Clear search
          </button>
        </div>
      )}

      {lists.map(list => (
        <WordListRow
          key={list.id}
          list={list}
          variant={variant}
          isSelected={selectedId === list.id}
          isInCollection={collectionIds.has(list.id)}
          isBusy={isBusy}
          {...rowActions}
        />
      ))}
    </section>
  );
};

export default WordListTable;
